import type { TourStep } from "@repo/dashboard-core";

const k = (id: string, suffix: string) => `steps.${id}.${suffix}`;

export const tourSteps: TourStep[] = [
  {
    id: "welcome",
    bannerKey: k("welcome", "banner"),
    iconName: "rocket",
    headingKey: k("welcome", "heading"),
    descriptionKey: k("welcome", "description"),
    blocks: [
      {
        kind: "video",
        provider: "youtube",
        videoId: "o2U47aCbBB4",
      },
    ],
  },
  {
    id: "documents",
    bannerKey: k("documents", "banner"),
    iconName: "folder",
    headingKey: k("documents", "heading"),
    descriptionKey: k("documents", "description"),
    blocks: [
      {
        kind: "illustration",
        src: "/tour/documents.svg",
        width: 192,
        height: 128,
        maxWidthClass: "max-w-[260px]",
      },
      {
        kind: "featureList",
        layout: "list",
        variant: "plain",
        items: [
          {
            iconName: "cloud",
            labelKey: k("documents", "features.cloud"),
          },
          {
            iconName: "refresh",
            labelKey: k("documents", "features.sync"),
          },
          {
            iconName: "folder",
            labelKey: k("documents", "features.organize"),
          },
        ],
      },
    ],
  },
  {
    id: "ai-chat",
    bannerKey: k("ai-chat", "banner"),
    iconName: "chatBot",
    headingKey: k("ai-chat", "heading"),
    descriptionKey: k("ai-chat", "description"),
    blocks: [
      {
        kind: "chipsChat",
        chips: [
          {
            iconName: "sparkle",
            labelKey: k("ai-chat", "chips.summarize"),
            active: true,
          },
          { labelKey: k("ai-chat", "chips.dates") },
          { labelKey: k("ai-chat", "chips.explain") },
          { labelKey: k("ai-chat", "chips.reply") },
        ],
        assistantBubbleKey: k("ai-chat", "assistant"),
        userBubbleKey: k("ai-chat", "user"),
        avatarSrc: "/tour/avatar-user.svg",
      },
    ],
  },
  {
    id: "pdf-tools",
    bannerKey: k("pdf-tools", "banner"),
    iconName: "wrench",
    headingKey: k("pdf-tools", "heading"),
    descriptionKey: k("pdf-tools", "description"),
    blocks: [
      {
        kind: "toolGrid",
        items: [
          {
            iconName: "fileText",
            labelKey: k("pdf-tools", "tools.convert.label"),
            subLabelKey: k("pdf-tools", "tools.convert.sub"),
          },
          {
            iconName: "copy",
            labelKey: k("pdf-tools", "tools.compress.label"),
            subLabelKey: k("pdf-tools", "tools.compress.sub"),
          },
          {
            iconName: "lock",
            labelKey: k("pdf-tools", "tools.protect.label"),
            subLabelKey: k("pdf-tools", "tools.protect.sub"),
          },
        ],
      },
    ],
  },
  {
    id: "cv-maker",
    bannerKey: k("cv-maker", "banner"),
    iconName: "fileText",
    headingKey: k("cv-maker", "heading"),
    descriptionKey: k("cv-maker", "description"),
    blocks: [
      {
        kind: "illustration",
        src: "/tour/cv-maker.svg",
        width: 195,
        height: 202,
        maxWidthClass: "max-w-[150px] sm:max-w-[260px]",
      },
      {
        kind: "featureList",
        layout: "list",
        variant: "grouped",
        items: [
          {
            iconName: "checkCircleFilled",
            labelKey: k("cv-maker", "features.templates"),
          },
          {
            iconName: "filePdf",
            labelKey: k("cv-maker", "features.export"),
          },
          {
            iconName: "share",
            labelKey: k("cv-maker", "features.share"),
          },
        ],
      },
    ],
  },
  {
    id: "forms",
    bannerKey: k("forms", "banner"),
    iconName: "clipboard",
    headingKey: k("forms", "heading"),
    descriptionKey: k("forms", "description"),
    blocks: [
      {
        kind: "searchCategories",
        searchPlaceholderKey: k("forms", "search-placeholder"),
        categories: [
          {
            iconName: "scale",
            tint: "amber",
            labelKey: k("forms", "categories.legal.label"),
            subLabelKey: k("forms", "categories.legal.sub"),
          },
          {
            iconName: "users",
            tint: "blue",
            labelKey: k("forms", "categories.hr.label"),
            subLabelKey: k("forms", "categories.hr.sub"),
          },
          {
            iconName: "receipt",
            tint: "green",
            labelKey: k("forms", "categories.finance.label"),
            subLabelKey: k("forms", "categories.finance.sub"),
          },
        ],
      },
    ],
  },
  {
    id: "doc-generator",
    bannerKey: k("doc-generator", "banner"),
    iconName: "fileText",
    headingKey: k("doc-generator", "heading"),
    descriptionKey: k("doc-generator", "description"),
    blocks: [
      {
        kind: "toggleSection",
        items: [
          {
            iconName: "fileText",
            tint: "amber",
            titleKey: k("doc-generator", "invoice.title"),
            illustrationSrc: "/tour/invoice-skeleton.svg",
            footerLabelKey: k("doc-generator", "invoice.footer"),
          },
          {
            iconName: "edit",
            tint: "blue",
            titleKey: k("doc-generator", "proposal.title"),
            illustrationSrc: "/tour/proposal-skeleton.svg",
            footerLabelKey: k("doc-generator", "proposal.footer"),
          },
        ],
      },
    ],
  },
  {
    id: "all-set",
    bannerKey: k("all-set", "banner"),
    iconName: "rocket",
    headingKey: k("all-set", "heading"),
    descriptionKey: k("all-set", "description"),
    blocks: [
      {
        kind: "featureList",
        layout: "list",
        variant: "circleRows",
        items: [
          {
            iconName: "cloud",
            labelKey: k("all-set", "features.cloud.label"),
            subLabelKey: k("all-set", "features.cloud.sub"),
          },
          {
            iconName: "chatBot",
            labelKey: k("all-set", "features.ai.label"),
            subLabelKey: k("all-set", "features.ai.sub"),
          },
          {
            iconName: "filePdf",
            labelKey: k("all-set", "features.tools.label"),
            subLabelKey: k("all-set", "features.tools.sub"),
          },
          {
            iconName: "edit",
            labelKey: k("all-set", "features.templates.label"),
            subLabelKey: k("all-set", "features.templates.sub"),
          },
        ],
      },
      {
        kind: "infoNote",
        iconName: "headphones",
        textKey: k("all-set", "note"),
      },
    ],
  },
];
